// File: src/pages/TodayPage.jsx
import { useMeetings, useTodayStatus } from '../hooks/useMeetings';
import MeetingCard from '../components/MeetingCard';
import { toPersian, getIranNow, getIranDayOfWeek, DAYS_OF_WEEK } from '../data/constants';

/* ── Spinner Sub-Component ─────────────────────── */
const Spinner = () => {
  return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div style={{ 
        width: 36, 
        height: 36, 
        border: '3px solid var(--border)', 
        borderTop: '3px solid var(--accent)',
        borderRadius: '50%', 
        animation: 'spin 1s linear infinite', 
        margin: '0 auto 14px' 
      }} />
      <p style={{ color: 'var(--text2)', fontSize: 13, fontWeight: 600 }}>در حال دریافت جلسات امروز...</p>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   Main Page Component
══════════════════════════════════════════════════ */
export default function TodayPage() {
  const now      = getIranNow();
  const dow      = getIranDayOfWeek(now);
  const dayName  = DAYS_OF_WEEK.find(d => d.id === dow)?.name || '';
  const iranTime = now.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });

  const { meetings, loading, error, refresh } = useMeetings({});
  const todayList = useTodayStatus(meetings);

  // فقط جلسات زنده یا تا ۱۰۰ دقیقه آینده
  const WINDOW_MIN = 100;
  const upcomingList = todayList.filter(m =>
    m.session_status === 'live' ||
    (m.session_status === 'upcoming' && (m.minutesLeft ?? 9999) <= WINDOW_MIN)
  );
  const liveCount = upcomingList.filter(m => m.session_status === 'live').length;
  const soonCount = upcomingList.filter(m => m.session_status === 'upcoming').length;

  if (loading && !meetings.length) return <Spinner />;

  return (
    <div style={{ padding: '0 0 8px', direction: 'rtl' }}>
      
      {/* تزریق متمرکز و بهینه انیمیشن‌های مورد نیاز صفحه در روت اصلی */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes ping { 75%, 100% { transform: scale(2); opacity: 0; } }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        /* تعریف رنگ داینامیک بنر بر اساس تم برای حذف تگ استایل شرطی */
        .live-banner-text { color: #065f46; }
        [data-theme="dark"] .live-banner-text { color: #a7f3d0; }
      `}</style>

      {/* 🟦 هدر کارت وضعیت امروز */}
      <div style={{
        background: 'var(--bg2)', 
        border: '1px solid var(--border)', 
        borderRadius: 20,
        padding: '18px 20px', 
        marginBottom: 16,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        flexWrap: 'wrap', 
        gap: 12,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)',
      }}>
        <div>
          <div style={{ 
            fontSize: 11, 
            color: 'var(--accent)', 
            fontWeight: 900, 
            letterSpacing: '.05em', 
            marginBottom: 4 
          }}>
            امروز
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--text)' }}>{dayName}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontWeight: 600 }}>
            {upcomingList.length > 0
              ? `${toPersian(upcomingList.length)} جلسه فعال یا تا ۱۰۰ دقیقه آینده`
              : `${toPersian(todayList.length)} جلسه امروز`}
          </div>
        </div>
        
        <div style={{ textAlign: 'left' }}>
          <div style={{ 
            fontSize: 32, 
            fontWeight: 950, 
            color: 'var(--text)', 
            direction: 'ltr', 
            lineHeight: 1 
          }}>
            {iranTime}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4, fontWeight: 700 }}>ساعت رسمی ایران</div>
        </div>
      </div>

      {/* 🟩 بنر داینامیک جلسات زنده (Live) */}
      {liveCount > 0 && (
        <div style={{
          display: 'flex', 
          alignItems: 'center', 
          gap: 10,
          background: 'rgba(16, 185, 129, 0.12)', 
          border: '1px solid rgba(16, 185, 129, 0.22)',
          borderRadius: 14, 
          padding: '10px 14px', 
          marginBottom: 16,
          flexWrap: 'wrap'
        }}>
          {/* آیکون پالس و سیگنال سبز */}
          <span style={{ position: 'relative', width: 9, height: 9, flexShrink: 0, display: 'inline-block' }}>
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#10b981', opacity: 0.6, animation: 'ping 1.5s ease infinite' }} />
            <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: '#10b981' }} />
          </span>
          
          <span style={{ fontSize: 13, fontWeight: 750 }} className="live-banner-text">
            {toPersian(liveCount)} جلسه هم‌اکنون در حال برگزاری است
          </span>
          
          <button onClick={refresh}
            style={{ 
              marginRight: 'auto', 
              fontSize: 11, 
              color: 'var(--text2)', 
              padding: '5px 12px',
              background: 'var(--bg3)',
              border: '1px solid var(--border)', 
              borderRadius: 8, 
              cursor: 'pointer',
              fontWeight: 700,
              transition: 'all 0.15s'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = 'var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            🔄 به‌روزرسانی
          </button>
        </div>
      )}

      {/* 🟥 مدیریت و نمایش خطا */}
      {error && (
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.06)', 
          border: '1px solid rgba(239, 68, 68, 0.15)',
          borderRadius: 14, 
          padding: '14px', 
          textAlign: 'center', 
          marginBottom: 16 
        }}>
          <p style={{ color: '#f87171', fontSize: 13, marginBottom: 8, fontWeight: 600 }}>⚠️ {error}</p>
          <button onClick={refresh} style={{ fontSize: 12, color: '#ef4444', fontWeight: 800, background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer' }}>تلاش مجدد</button>
        </div>
      )}

      {/* 📭 لیست نهایی کارت‌ها یا نمایش وضعیت خالی */}
      {upcomingList.length === 0 && !loading ? (
        <div style={{ padding: '70px 20px', textAlign: 'center', opacity: 0.5 }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🕐</div>
          <p style={{ color: 'var(--text2)', fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
            در ۱۰۰ دقیقه آینده جلسه‌ای نیست
          </p>
          {todayList.length > 0 && (
            <p style={{ color: 'var(--text3)', fontSize: 12 }}>
              امروز در مجموع {toPersian(todayList.length)} جلسه دارید — برای دیدن همه به تب «همه» بروید
            </p>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {upcomingList.map((m, i) => (
            <div key={m.id} style={{ animation: `slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${Math.min(i, 8) * 0.04}s both` }}>
              <MeetingCard meeting={m} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}